import {
  ElementController,
  debounce,
  selectFrom,
  selectSingleChild,
} from 'js-common';

import { MENU_CONTROLLER_ID } from './constants';

/**
 * Handles click outside/inside toggled menu on smaller screens
 */
export default class Menu extends ElementController {
  static id = MENU_CONTROLLER_ID;

  initialState = {
    isHoldingModifierKey: false,
  };

  /**
   * DOM loaded, initialize elements and bind events.
   */
  init() {
    this.mainNav = this.getSingleNode('main-nav');
    this.menuToggle = this.getSingleNode('menu-toggle');
    this.menuToggleStyle = window.getComputedStyle(this.menuToggle);
    this.menuList = selectSingleChild(this.mainNav, 'ul');
    this.lastMainNavLink = selectFrom(this.mainNav, 'a').pop();

    this.on(this.mainNav, 'click', this.handleMainNavClick);
    this.on(this.mainNav, 'keydown', this.handleMainNavKeydown);
    this.on(this.lastMainNavLink, 'blur', this.handleMainNavLastBlur);
    this.on(this.menuToggle, 'blur', this.handleMenuToggleBlur);

    this.checkMenuToggleState();
    this.on(window, 'resize', debounce(this.handleResize, 200));
  }

  /**
   * Check if the menu is currently in its toggled state.
   */
  checkMenuToggleState = () => {
    this.isToggledMenu = this.menuToggleStyle.display !== 'none';
  };

  /**
   * Set focus to the specified element if currently focused on something
   * outside the main nav.
   *
   * @param {Element} rootNode - Node to check if outside of.
   * @param {Element} focusNode - Node to set focus to if relevant.
   */
  setFocusIfOutside = (rootNode, focusNode) => {
    setTimeout(() => {
      if (!rootNode.contains(document.activeElement)) {
        focusNode.focus();
      }
    });
  };

  /**
   * Check toggled menu state on resize.
   */
  handleResize = () => {
    this.checkMenuToggleState();
  };

  /**
   * Close the toggled menu when clicking on the 'background overlay', which is
   * actually just the menu background.
   *
   * @param {object} e - Click event.
   */
  handleMainNavClick = (e) => {
    if (
      this.isToggledMenu &&
      // Not clicking on something inside the list
      !this.menuList.contains(e.target)
    ) {
      this.menuToggle.click();
    }
  };

  /**
   * Close the toggled menu when pressing escape.
   *
   * @param {object} e - Keydown event.
   */
  handleMainNavKeydown = (e) => {
    if (this.isToggledMenu && e.key === 'Escape') {
      this.menuToggle.click();
      this.menuToggle.focus();
    }
  };

  /**
   * Focus the last link if leaving the menu toggle.
   */
  handleMenuToggleBlur = () => {
    if (this.isToggledMenu) {
      this.setFocusIfOutside(this.mainNav, this.lastMainNavLink);
    }
  };

  /**
   * Focus the menu toggle if leaving the last link.
   */
  handleMainNavLastBlur = () => {
    if (this.isToggledMenu) {
      this.setFocusIfOutside(this.mainNav, this.menuToggle);
    }
  };
}
